import type { Ranges } from '@deighton/shared-ui/core';

import type { GridSortDirection } from './grid-filter-descriptor.interface';

/** Default column width in pixels when no width is specified. */
export const DEFAULT_COLUMN_WIDTH: number = 180;

/** Minimum column width in pixels (drag-resize constraint). */
export const MIN_COLUMN_WIDTH: number = 60;

/** Maximum auto-sized column width in pixels. */
export const MAX_AUTO_COLUMN_WIDTH: number = 300;

/**
 * Default Angular DatePipe format string used when a column does not specify a custom `dateFormat`.
 * @see https://angular.dev/api/common/DatePipe
 */
export const DEFAULT_DATE_FORMAT: string = 'mediumDate';

/** Data type for a grid column — drives formatting, filtering, and cell rendering. */
export type DataGridColumnDataType = 'text' | 'numeric' | 'date' | 'boolean';

/** Active sort state for a group or flat view. */
export interface DataGridGroupSortState {
  readonly field: string;
  readonly dir: GridSortDirection;
}

/** Section in the columns popover. */
export interface DataGridColumnSection {
  /** Stable key for expand/collapse state and @for tracking. */
  readonly sectionId: string;
  readonly icon: string;
  readonly columns: Array<DataGridColumn>;
  /** Human-friendly type name for type-specific sections (drives i18n in the template). */
  readonly typeDisplayName?: string;
}

/** Column definition for the data grid. */
export interface DataGridColumn {
  /** Display header text. */
  header: string;
  /** Field name in the data record. */
  field: string;
  /** Data type for formatting and filtering. */
  type: DataGridColumnDataType;
  /** Optional unit suffix (e.g., 'ft', 'IRI', 'tons'). */
  unit?: string;
  /** Width in pixels. */
  width?: number;
  /** Whether this column is the discriminator that splits data by scope. */
  isDiscriminator?: boolean;
  /** Whether the column is hidden by default. */
  hidden?: boolean;
  /** Discriminator scope this column belongs to (undefined = shared across all scopes). */
  discriminatorScope?: string;
  /**
   * Optional condition-based color ranges for numeric columns.
   * Reuses the shared `Ranges` type and `getCategoryForValue()` helper
   * from `@deighton/shared-ui/core`.
   */
  colorRanges?: Array<Ranges>;
  /**
   * When true (default), the d-chip displays the category name (e.g., "85: Good").
   * When false, the chip shows only the numeric value with the range-colored background.
   */
  showCategoryLabel?: boolean;
  /** Optional text categories for status-style badge columns. */
  textCategories?: Array<Omit<Ranges, 'range'>>;
  /** Angular DatePipe format string for date-type columns. */
  dateFormat?: string;
  /**
   * When `true`, cell values are domain value objects (`{ value, displayValue }`)
   * that must be unwrapped for display and sorting.
   */
  isDomainValue?: boolean;
}

/**
 * Per-scope presentation override for a shared column when the
 * discriminator is in the group chain (grouped leaf tables only).
 */
export interface GroupedColumnOverride {
  /** Replaces the shared column header for this discriminator scope's leaf table. */
  header?: string;
  /** When true, the column is omitted from this scope's grouped leaf table. */
  hidden?: boolean;
}
